'use client';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Leaf, ArrowRight, ShieldCheck, Zap, Globe } from 'lucide-react';

export default function HeroSection() {
  const router = useRouter();

  const handleSaveDemo = () => {
    toast?.success('বিশ্লেষণ সংরক্ষণ করতে প্রথমে লগইন করুন।', {
      action: { label: 'লগইন', onClick: () => router?.push('/login') },
    });
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-amber-50 pt-14 pb-16 lg:pt-24 lg:pb-28">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-72 h-72 sm:w-96 sm:h-96 bg-green-100 rounded-full opacity-40 blur-3xl animate-float" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 sm:w-80 sm:h-80 bg-amber-100 rounded-full opacity-30 blur-3xl" style={{ animation: 'float 4s ease-in-out infinite 1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[600px] sm:h-[600px] bg-green-50 rounded-full opacity-20 blur-3xl" />
      </div>
      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="slide-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-green-100 border border-green-200 text-green-700 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 rounded-full mb-5 hover:bg-green-200 transition-all duration-200 cursor-default">
              <Zap size={13} className="text-amber-500 animate-pulse" />
              AI-চালিত ফসল সুরক্ষা
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight tracking-tight mb-4 sm:mb-6">
              ফসলের রোগ{' '}
              <span className="text-green-600 relative">
                তাৎক্ষণিক
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 8" fill="none">
                  <path d="M0 6 Q50 0 100 4 Q150 8 200 2" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.4"/>
                </svg>
              </span>{' '}
              শনাক্ত করুন
            </h1>

            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8 max-w-xl">
              ফসলের পাতার ছবি তুলুন — AgroGuard AI সেকেন্ডের মধ্যে রোগ শনাক্ত করে, তীব্রতা মাপে এবং সঠিক চিকিৎসা পরামর্শ দেয়। বাংলায় সম্পূর্ণ সহায়তা।
            </p>

            {/* CTA Buttons - full width on mobile */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8 sm:mb-10">
              <Link
                href="/crop-analysis-page"
                className="btn-primary flex items-center justify-center gap-2 text-base py-4 sm:py-3 px-6 btn-ripple group rounded-2xl"
              >
                <Leaf size={18} className="transition-transform duration-300 group-hover:rotate-12" />
                ফসল বিশ্লেষণ করুন
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                href="/ai-chat-page"
                className="btn-secondary flex items-center justify-center gap-2 text-base py-4 sm:py-3 px-6 btn-ripple rounded-2xl"
              >
                AI-কে জিজ্ঞেস করুন
              </Link>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-5 stagger-children">
              {[
                { icon: ShieldCheck, text: '৯৪% নির্ভুলতা' },
                { icon: Zap, text: '৩ সেকেন্ডে ফলাফল' },
                { icon: Globe, text: '১২,০০০+ কৃষক' },
              ]?.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-600 hover:text-green-700 transition-colors duration-200 cursor-default bg-white/70 px-3 py-1.5 rounded-full border border-green-100 shadow-sm">
                  <item.icon size={15} className="text-green-600" />
                  <span className="font-semibold">{item?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: AI Demo Card */}
          <div className="relative fade-in mt-4 lg:mt-0">
            <div className="bg-white rounded-3xl shadow-xl border border-green-100 overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 mx-auto max-w-sm sm:max-w-none">
              {/* Header */}
              <div className="bg-green-600 px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <span className="text-white/80 text-xs sm:text-sm font-medium">AgroGuard AI বিশ্লেষণ</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
                  <span className="text-white/70 text-xs">সক্রিয়</span>
                </div>
              </div>

              {/* Image preview */}
              <div className="relative h-40 sm:h-48 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                  backgroundImage: 'radial-gradient(circle at 30% 40%, #16a34a 1px, transparent 1px), radial-gradient(circle at 70% 60%, #15803d 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }} />
                <div className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-600/20 rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-3 border-2 border-green-300 border-dashed hover:border-green-500 hover:bg-green-600/30 transition-all duration-300">
                    <Leaf size={30} className="text-green-600 float" />
                  </div>
                  <p className="text-green-700 text-sm font-medium">ধানের পাতা — বিশ্লেষণ সম্পন্ন</p>
                </div>
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-green-400 to-transparent animate-pulse" />
              </div>

              {/* Results */}
              <div className="p-4 sm:p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">শনাক্তকৃত রোগ</p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900">ধানের ব্লাস্ট রোগ</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">নির্ভুলতা</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600 font-mono">92%</p>
                  </div>
                </div>

                {/* Severity bar */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">তীব্রতা</span>
                    <span className="badge-medium">মাঝারি (৪৫%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full transition-all duration-1000" style={{ width: '45%' }} />
                  </div>
                </div>

                {/* Treatment preview */}
                <div className="bg-green-50 rounded-xl p-3 border border-green-100 hover:border-green-300 transition-colors duration-200">
                  <p className="text-xs font-semibold text-green-700 mb-1">✅ পরামর্শ</p>
                  <p className="text-sm text-gray-700">Tricyclazole ৭৫% WP ছত্রাকনাশক ব্যবহার করুন। জৈব বিকল্প: নিম পাতার নির্যাস।</p>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Link href="/crop-analysis-page" className="btn-primary text-sm py-3 text-center btn-ripple rounded-xl">বিস্তারিত দেখুন</Link>
                  <button onClick={handleSaveDemo} className="btn-secondary text-sm py-3 text-center btn-ripple rounded-xl">সংরক্ষণ করুন</button>
                </div>
              </div>
            </div>

            {/* Floating tags */}
            <div className="absolute -top-3 -right-2 sm:-top-4 sm:-right-4 bg-amber-400 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg rotate-3 hover:rotate-0 hover:scale-110 transition-all duration-200 cursor-default">
              AI-চালিত
            </div>
            <div className="absolute -bottom-3 -left-2 sm:-bottom-3 sm:-left-3 bg-white border border-green-200 text-green-700 text-xs font-semibold px-3 py-2 rounded-xl shadow-md flex items-center gap-1.5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-default">
              <ShieldCheck size={13} />
              ৩ সেকেন্ডে ফলাফল
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}